// Main dashboard component with chat interface
import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import {
  Box,
  Drawer,
  Typography,
  IconButton,
  Button,
  Divider,
  List,
  Menu,
  MenuItem,
  Alert,
  Tooltip,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Circle as CircleIcon,
  ClearAll as ClearAllIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
// import { useAuth } from '../../contexts/AuthContext';
import { useChat } from '../../contexts/ChatContext';
import { useTranslation } from 'react-i18next';
import ChatInterface from './ChatInterface';
// import LanguageSwitcher from '../common/LanguageSwitcher';
import ConversationItem from '../common/ConversationItem';
import ConfirmDialog from '../common/ConfirmDialog';
import ModernHeader from '../common/ModernHeader';

const DRAWER_WIDTH = 280;

const Dashboard: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [conversationsMenuAnchor, setConversationsMenuAnchor] = useState<null | HTMLElement>(null);
  const [showClearAllDialog, setShowClearAllDialog] = useState(false);
  const [isClearingAll, setIsClearingAll] = useState(false);
  
  // const { user, logout } = useAuth(); // Not used in this component
  const { t } = useTranslation();
  const { 
    conversations, 
    currentConversation, 
    loadConversations, 
    createConversation, 
    updateConversation,
    deleteConversation,
    clearAllConversations,
    isLoading,
    isConnected,
    error,
    clearError 
  } = useChat();
  
  const navigate = useNavigate();
  
  // Track if we've attempted to load conversations to prevent infinite loop
  const [hasAttemptedLoad, setHasAttemptedLoad] = useState(false);

  // Load conversations on mount
  useEffect(() => {
    if (conversations.length === 0 && !isLoading && !hasAttemptedLoad) {
      console.log('🔄 Loading conversations for first time...');
      loadConversations().finally(() => {
        setHasAttemptedLoad(true);
        console.log('✅ Initial conversation load attempt completed');
      });
    }
  }, [conversations.length, isLoading, hasAttemptedLoad]); // Added hasAttemptedLoad to prevent infinite loop

  // Auto-select first conversation if none selected
  useEffect(() => {
    if (conversations.length > 0 && !currentConversation) {
      const firstConversation = conversations[0];
      navigate(`/dashboard/chat/${firstConversation.id}`);
    }
  }, [conversations.length, currentConversation, navigate]); // Use conversations.length instead of conversations

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };


  const handleNewConversation = async () => {
    try {
      const newConversation = await createConversation({
        title: t('conversations.newConversation'),
      });
      // Reset the attempted load flag so we can load conversations again
      setHasAttemptedLoad(false);
      navigate(`/dashboard/chat/${newConversation.id}`);
      setMobileOpen(false);
    } catch (error) {
      console.error('Failed to create conversation:', error);
    }
  };

  const handleConversationSelect = (conversationId: string) => {
    navigate(`/dashboard/chat/${conversationId}`);
    setMobileOpen(false);
  };

  const handleConversationsMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setConversationsMenuAnchor(event.currentTarget);
  };

  const handleConversationsMenuClose = () => {
    setConversationsMenuAnchor(null);
  };

  const handleClearAllStart = () => {
    setShowClearAllDialog(true);
    handleConversationsMenuClose();
  };

  const handleClearAllConfirm = async () => {
    setIsClearingAll(true);
    try {
      await clearAllConversations();
      setShowClearAllDialog(false);
      // Navigate to dashboard root after clearing all
      navigate('/dashboard');
    } catch (error) {
      console.error('Failed to clear all conversations:', error);
    } finally {
      setIsClearingAll(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = (now.getTime() - date.getTime()) / (1000 * 60);

    if (diffInMinutes < 1) {
      return t('time.now');
    } else if (diffInMinutes < 60) {
      return t('time.minutesAgo', { count: Math.floor(diffInMinutes) });
    } else if (diffInMinutes < 24 * 60) {
      return t('time.hoursAgo', { count: Math.floor(diffInMinutes / 60) });
    } else if (diffInMinutes < 7 * 24 * 60) {
      return t('time.daysAgo', { count: Math.floor(diffInMinutes / (24 * 60)) });
    } else {
      return date.toLocaleDateString();
    }
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header with Logo */}
      <Box sx={{ p: 2 }}>
        {/* Logo in Sidebar - Star Image + Montserrat Text */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'flex-start', // Left-aligned
          alignItems: 'center',
          gap: 2, // Space between star and text
          mb: 2,
        }}>
          <img 
            src="/logo-star.png" 
            alt="MED.IA Star"
            style={{ 
              height: 48,  // Match text height
              width: 'auto',
              objectFit: 'contain',
            }}
            onError={(e) => {
              console.log('❌ Star logo failed to load, using fallback');
              // Hide broken image and show fallback
              e.currentTarget.style.display = 'none';
              const fallbackElement = e.currentTarget.parentNode?.querySelector('.logo-fallback') as HTMLElement;
              if (fallbackElement) {
                fallbackElement.style.display = 'block';
              }
            }}
          />
          
          {/* MED.IA Text with Montserrat Font */}
          <Typography
            variant="h3"
            component="div"
            sx={{
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 700, // Bold
              fontSize: '2.5rem', // Large size to match star height
              color: '#212529', // Brand dark gray
              letterSpacing: '-0.02em',
              lineHeight: 1,
            }}
          >
            MED.IA
          </Typography>
          
          {/* Fallback star icon if image fails */}
          <Box
            className="logo-fallback"
            sx={{
              display: 'none', // Hidden by default, shown on image error
              width: 48,
              height: 48,
              background: 'linear-gradient(45deg, #7dd3fc 0%, #06b6d4 25%, #0891b2 50%, #0e7490 75%, #155e75 100%)',
              borderRadius: '20%',
              transform: 'rotate(45deg)',
            }}
          />
        </Box>

        {/* Connection Status under logo - Left-aligned */}
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-start' }}>
          <Chip
            icon={<CircleIcon sx={{ fontSize: 10 }} />}
            label={isConnected ? t('chat.connected') : t('chat.disconnected')}
            size="small"
            color={isConnected ? 'success' : 'error'}
            variant="outlined"
            sx={{
              borderRadius: 1,
              fontSize: '0.7rem',
              height: 20,
              '& .MuiChip-label': { px: 0.5 },
              '& .MuiChip-icon': { fontSize: 8, ml: 0.25 },
              opacity: 0.8,
            }}
          />
        </Box>
        
        <Button
          fullWidth
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleNewConversation}
          disabled={isLoading}
          sx={{
            borderRadius: 2,
            py: 1.5,
            fontSize: '0.875rem',
            fontWeight: 600,
            textTransform: 'none',
          }}
        >
          {t('conversations.newConversation')}
        </Button>
      </Box>

      <Divider />
      
      {/* Conversations Header with Menu */}
      <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="subtitle2" color="text.secondary">
          {t('conversations.title')}
        </Typography>
        {conversations.length > 0 && (
          <Tooltip title={t('conversations.clearAll')}>
            <IconButton
              size="small"
              onClick={handleConversationsMenuClick}
              sx={{ opacity: 0.7, '&:hover': { opacity: 1 } }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Conversations List */}
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        {error && (
          <Alert 
            severity="error" 
            sx={{ m: 1 }}
            onClose={clearError}
          >
            {error}
          </Alert>
        )}
        
        <List dense>
          {conversations.map((conversation) => (
            <ConversationItem
              key={conversation.id}
              conversation={conversation}
              isSelected={currentConversation?.id === conversation.id}
              onSelect={handleConversationSelect}
              onDelete={deleteConversation}
              onUpdate={updateConversation}
              formatDate={formatDate}
            />
          ))}
        </List>

        {conversations.length === 0 && !isLoading && (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {t('conversations.empty')}
              <br />
              {t('conversations.emptyDescription')}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      {/* Modern Header */}
      <ModernHeader onMenuToggle={handleDrawerToggle} />

      {/* Main Content Area */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

      {/* Navigation Drawer */}
      <Box
        component="nav"
        sx={{ width: { sm: DRAWER_WIDTH }, flexShrink: { sm: 0 } }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
            },
          }}
        >
          {drawer}
        </Drawer>
        
        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

        {/* Main content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            height: '100%',
            overflow: 'hidden',
          }}
        >
          <Routes>
            <Route path="/" element={<ChatInterface />} />
            <Route path="/chat/:conversationId" element={<ChatInterface />} />
          </Routes>
        </Box>
      </Box>

      {/* Conversations Context Menu */}
      <Menu
        anchorEl={conversationsMenuAnchor}
        open={Boolean(conversationsMenuAnchor)}
        onClose={handleConversationsMenuClose}
        PaperProps={{
          sx: { minWidth: 160 }
        }}
      >
        <MenuItem onClick={handleClearAllStart} sx={{ color: 'error.main' }}>
          <ClearAllIcon sx={{ mr: 2 }} fontSize="small" />
          {t('conversations.clearAll')}
        </MenuItem>
      </Menu>

      {/* Clear All Confirmation Dialog */}
      <ConfirmDialog
        open={showClearAllDialog}
        onClose={() => setShowClearAllDialog(false)}
        onConfirm={handleClearAllConfirm}
        title={t('conversations.clearAllConfirmTitle')}
        message={t('conversations.clearAllConfirmMessage')}
        confirmText={t('conversations.clearAll')}
        severity="error"
        isLoading={isClearingAll}
      />
    </Box>
  );
};

export default Dashboard;
